<template>
  <Dialog :open="show" @update:open="onDialogUpdate">
    <DialogScrollContent size="md" class="tw:gap-6">
      <DialogHeader>
        <DialogTitle>{{ $t('Invitation details') }}</DialogTitle>
      </DialogHeader>

      <div class="tw:space-y-6">
        <p class="tw:text-sm tw:text-zinc-600">
          {{ $t('This member cannot sign in until they have chosen their password from the invitation email. Their permissions can be edited in the meantime.') }}
        </p>

        <div class="tw:bg-zinc-50/70 tw:border tw:border-zinc-200/50 tw:rounded tw:px-4">
          <dl class="tw:grid tw:grid-cols-1 tw:text-base/6 tw:sm:grid-cols-[min(30%,--spacing(80))_auto] tw:sm:text-sm/6">
            <dt :class="termClass">{{ $t('Status') }}</dt>
            <dd :class="descriptionClass">
              <span v-if="isExpired" class="badge badge-danger">{{ $t('Invitation expired') }}</span>
              <span v-else class="badge badge-info">{{ $t('Pending invitation') }}</span>
            </dd>

            <template v-if="user?.email">
              <dt :class="termClass">{{ $t('Email') }}</dt>
              <dd :class="descriptionClass" class="tw:break-all">{{ user.email }}</dd>
            </template>

            <template v-if="invitation?.invited_by">
              <dt :class="termClass">{{ $t('Invited by') }}</dt>
              <dd :class="descriptionClass">{{ invitation.invited_by }}</dd>
            </template>

            <template v-if="sentOn">
              <dt :class="termClass">{{ $t('Sent on') }}</dt>
              <dd :class="descriptionClass">{{ sentOn }}</dd>
            </template>

            <template v-if="expiresOn">
              <dt :class="termClass">{{ isExpired ? $t('Expired on') : $t('Expires on') }}</dt>
              <dd :class="descriptionClass">{{ expiresOn }}</dd>
            </template>
          </dl>
        </div>

        <p v-if="!readOnly" class="tw:text-sm tw:text-zinc-600">
          {{ $t('Sending the invitation again issues a new link and invalidates the previous one.') }}
        </p>
      </div>

      <DialogFooter>
        <Button type="button" variant="secondary" data-testid="close" @click="emits('closed')">
          {{ $t('Close') }}
        </Button>
        <Button
          v-if="!readOnly"
          type="button"
          data-testid="resend"
          :loading="isResending"
          @click="emits('resend')"
        >
          {{ $t('Resend the invitation') }}
        </Button>
      </DialogFooter>
    </DialogScrollContent>
  </Dialog>
</template>

<script setup>
import { computed } from 'vue';
import {
  Dialog,
  DialogScrollContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/shadcn/components/ui/button';
import { displayDate } from '@/utils/date';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  user: {
    default: () => ({}),
  },
  isResending: {
    type: Boolean,
    default: false,
  },
  readOnly: {
    type: Boolean,
    default: false,
  },
});

const emits = defineEmits(['closed', 'resend']);

const termClass = 'tw:col-start-1 tw:border-t tw:border-zinc-950/5 tw:pt-3 tw:text-zinc-500 tw:first:border-none tw:sm:border-t tw:sm:border-zinc-950/5 tw:sm:py-3 tw:dark:border-white/5 tw:dark:text-zinc-400 tw:sm:dark:border-white/5';
const descriptionClass = 'tw:pt-1 tw:pb-3 tw:text-zinc-950 tw:sm:border-t tw:sm:border-zinc-950/5 tw:sm:py-3 tw:sm:nth-2:border-none tw:dark:text-white tw:dark:sm:border-white/5';

const invitation = computed(() => props.user?.pending_invitation ?? null);

const isExpired = computed(() => invitation.value?.is_expired === true);

const formatDate = (value, withTime) => (
  value ? displayDate(value, withTime) : null
);

// The exact time matters here: sending again is rate limited.
const sentOn = computed(() => formatDate(invitation.value?.sent_at, true));

const expiresOn = computed(() => formatDate(invitation.value?.expires_at, false));

const onDialogUpdate = (isOpen) => {
  if (!isOpen) {
    emits('closed');
  }
};
</script>
